import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiQueryWithToken, apiQueryWithTokenRegional } from "../../../common/services/apiBaseUrlConfig";
import { getAccount } from "../../../common/services/commonService";

const initialState = {
    data: [],
    status: "idle",
    error: null,
    completeQualificationDetailsByGuid: {},
    completeQualificationStatusByGuid: {},
    completeQualificationSaveStatusByGuid: {},
    completeQualificationErrorByGuid: {},
    sspQuestions: [],
    sspQuestionsStatus: "idle",
    sspQuestionsError: null,
};

const COMPLETE_QUALIFICATION_PAGE = "complete_qualification_page";

const toBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value || "").toLowerCase().trim();
    if (normalized === "1" || normalized === "true" || normalized === "yes") return true;
    if (normalized === "0" || normalized === "false" || normalized === "no") return false;
    return value;
};

const normalizeFinalDecision = (value) => {
    const normalized = String(value || "").toLowerCase().trim();
    if (normalized === "is pending") return "Pending";
    if (normalized === "pending") return "Pending";
    if (normalized === "in progress") return "In progress";
    if (normalized === "completed") return "Completed";
    if (normalized === "not qualified") return "Not Qualified";
    return value || "Pending";
};

const normalizeQuestionId = (value, fallback) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
    return fallback;
};

const normalizeQuestionAnswer = (item = {}, index = 0) => {
    const normalizedQuestionId = normalizeQuestionId(item?.questionId, index + 1);
    if (!normalizedQuestionId) return null;

    return {
        questionId: normalizedQuestionId,
        answer: toBoolean(item?.answer),
        engagementIdGuid: item?.engagementIdGuid || item?.engagementId_GUID || item?.engagementIdGUID || "",
    };
};

const normalizeQuestionAnswers = (questionAnswers = []) => {
    if (!Array.isArray(questionAnswers)) return [];
    return questionAnswers
        .map((item, index) => normalizeQuestionAnswer(item, index))
        .filter(Boolean);
};

const getQuestionAnswerById = (questionAnswers = [], questionId) => {
    const normalizedId = normalizeQuestionId(questionId, null);
    if (!normalizedId) return undefined;
    const match = questionAnswers.find((item) => normalizeQuestionId(item?.questionId, null) === normalizedId);
    return match?.answer;
};

const normalizeQuestion = (item, index) => {
    if (typeof item === "string") {
        const question = String(item || "").replace(/\s+/g, " ").trim();
        if (!question) return null;
        return {
            questionId: index + 1,
            question,
            page: COMPLETE_QUALIFICATION_PAGE,
            questionInfo: question,
        };
    }

    if (!item || typeof item !== "object") return null;

    const question = String(item?.question || "").replace(/\s+/g, " ").trim();
    if (!question) return null;

    return {
        questionId: normalizeQuestionId(item?.questionId, index + 1),
        question,
        page: String(item?.page || "").toLowerCase().trim() || COMPLETE_QUALIFICATION_PAGE,
        questionInfo: String(item?.questionInfo || "").replace(/\s+/g, " ").trim() || question,
    };
};

const normalizeEngagement = (item = {}) => ({
    ...(() => {
        const normalizedQuestionAnswers = normalizeQuestionAnswers(item?.questionAnswers);
        const hasQuestionAnswers = normalizedQuestionAnswers.length > 0;

        const legacyQuestionAnswers = [
            { questionId: 1, answer: toBoolean(item?.nonGPSClient) },
            { questionId: 2, answer: toBoolean(item?.isEYPrimeFirm) },
            { questionId: 3, answer: toBoolean(item?.isEngagementServicesEnded) },
            { questionId: 4, answer: toBoolean(item?.isEPApproved) },
        ].filter((answer) => answer.answer === true || answer.answer === false);

        const resolvedQuestionAnswers = normalizedQuestionAnswers.length > 0 ? normalizedQuestionAnswers : legacyQuestionAnswers;

        const fallbackNonGPSClient = getQuestionAnswerById(resolvedQuestionAnswers, 1);
        const fallbackIsEYPrimeFirm = getQuestionAnswerById(resolvedQuestionAnswers, 2);
        const fallbackIsEngagementServicesEnded = getQuestionAnswerById(resolvedQuestionAnswers, 3);
        const fallbackIsEPApproved = getQuestionAnswerById(resolvedQuestionAnswers, 4);

        const resolveToggleValue = (fieldName, fallbackValue) => {
            const hasField = Object.prototype.hasOwnProperty.call(item, fieldName);
            if (!hasField) return toBoolean(fallbackValue);

            const rawValue = item?.[fieldName];
            if (rawValue === null) {
                return hasQuestionAnswers ? toBoolean(fallbackValue) : null;
            }

            return toBoolean(rawValue);
        };

        return {
            ...item,
            engagementId_GUID: item.engagementId_GUID || item.engagementIdGUID || "",
            nonGPSClient: resolveToggleValue("nonGPSClient", fallbackNonGPSClient),
            isEYPrimeFirm: resolveToggleValue("isEYPrimeFirm", fallbackIsEYPrimeFirm),
            isEngagementServicesEnded: resolveToggleValue("isEngagementServicesEnded", fallbackIsEngagementServicesEnded),
            isEPApproved: resolveToggleValue("isEPApproved", fallbackIsEPApproved),
            questionAnswers: resolvedQuestionAnswers,
            finalDecision: normalizeFinalDecision(item.finalDecision),
            engagementManager:
                item.engagementManager || item.engagementManagerName || item.emName || item.engagementManagerEmail || "",
            engagementPartner: item.engagementPartner || item.epName || item.epEmail || "",
            partnerName: item.partnerName || item.epName || item.epEmail || "",
        };
    })(),
});

const getBaseURLByArea = (area) => {
    const normalizedArea = String(area || "").toLowerCase().trim();
    if (normalizedArea === "americas") return process.env.REACT_APP_API_URL_US;
    if (normalizedArea === "emeia") return process.env.REACT_APP_API_URL_EU;
    if (normalizedArea === "apac") return process.env.REACT_APP_API_URL_AU;
    return "";
};

const normalizeTransactionType = (value) => {
    const normalized = String(value || "").toLowerCase().trim();
    if (normalized === "buy-side" || normalized === "buy side") return "Buy-side";
    if (normalized === "sell-side" || normalized === "sell side") return "Sell-side";
    return value || "";
};

const normalizeCompleteQualificationDetails = (item = {}) => ({
    ...(() => {
        const normalizedQuestionAnswers = normalizeQuestionAnswers(item?.questionAnswers);
        const fallbackCanOverrideCICClause = getQuestionAnswerById(normalizedQuestionAnswers, 1);
        const fallbackCanDataTransfer = getQuestionAnswerById(normalizedQuestionAnswers, 2);
        const fallbackHaveActiveLegalHold = getQuestionAnswerById(normalizedQuestionAnswers, 3);

        return {
            engagementId_GUID: item.engagementId_GUID || item.engagementIdGUID || "",
            transactionType: normalizeTransactionType(item.transactionType),
            targetEntity: Object.prototype.hasOwnProperty.call(item, "targetEntity")
                ? (item.targetEntity ?? "")
                : undefined,
            isTransactionClosed: toBoolean(item.isTransactionClosed),
            isEvaluatedAssetAquired: toBoolean(item.isEvaluatedAssetAquired),
            canOverrideCICClause: toBoolean(item?.canOverrideCICClause ?? fallbackCanOverrideCICClause),
            canDataTransfer: toBoolean(item?.canDataTransfer ?? fallbackCanDataTransfer),
            haveActiveLegalHold: toBoolean(item?.haveActiveLegalHold ?? fallbackHaveActiveLegalHold),
            questionAnswers: normalizedQuestionAnswers,
            comments: item.comments || "",
            dataUploaders: item.dataUploaders || "",
        };
    })(),
});

export const fetchEngagementQualification = createAsyncThunk(
    "partnerFileUpload/fetchEngagementQualification",
    async (requestPayload = {}) => {
        const emailId = getAccount()?.username || "";
        const isEngagementAdmin =
            requestPayload?.isEngagementAdmin === null || requestPayload?.isEngagementAdmin === undefined
                ? ""
                : requestPayload?.isEngagementAdmin;
        const response = await apiQueryWithToken.post("/api/SSP/get/engagementQualification", {
            emailId,
            isEngagementAdmin,
        });
        const responsePayload =
            response?.data?.data ||
            response?.data?.result ||
            response?.data;
        return Array.isArray(responsePayload) ? responsePayload.map(normalizeEngagement) : [];
    }
);

export const editEngagementQualification = createAsyncThunk(
    "partnerFileUpload/editEngagementQualification",
    async ({ area, payload }, { rejectWithValue }) => {
        try {
            const baseURL = getBaseURLByArea(area);
            if (!baseURL) {
                return rejectWithValue("Invalid engagement area");
            }

            const requestPayload = {
                ...(payload || {}),
                Area: area,
            };

            const response = await apiQueryWithTokenRegional.post(
                `${baseURL}/api/SSP/edit/engagementQualification`,
                requestPayload
            );

            if (response?.data !== true) {
                return rejectWithValue("Edit engagement qualification failed");
            }

            return payload;
        } catch (error) {
            return rejectWithValue(error?.response?.data || error?.message || "Edit engagement qualification failed");
        }
    }
);

export const fetchCompleteQualificationDetails = createAsyncThunk(
    "partnerFileUpload/fetchCompleteQualificationDetails",
    async ({ area, engagementId_GUID }, { rejectWithValue }) => {
        try {
            const baseURL = getBaseURLByArea(area);
            if (!baseURL) {
                return rejectWithValue({ engagementId_GUID, message: "Invalid engagement area" });
            }

            const response = await apiQueryWithTokenRegional.post(
                `${baseURL}/api/SSP/get/completeQualificationDetails`,
                { engagementId_GUID }
            );

            const rawPayload =
                response?.data?.data ||
                response?.data?.result ||
                response?.data;

            const payload = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;

            return {
                engagementId_GUID,
                details: normalizeCompleteQualificationDetails(payload || {}),
            };
        } catch (error) {
            return rejectWithValue({
                engagementId_GUID,
                message: error?.response?.data || error?.message || "Failed to fetch complete qualification details",
            });
        }
    }
);

export const editCompleteQualificationDetails = createAsyncThunk(
    "partnerFileUpload/editCompleteQualificationDetails",
    async ({ area, payload }, { rejectWithValue }) => {
        try {
            const baseURL = getBaseURLByArea(area);
            if (!baseURL) {
                return rejectWithValue({ engagementId_GUID: payload?.engagementId_GUID, message: "Invalid engagement area" });
            }

            const response = await apiQueryWithTokenRegional.post(
                `${baseURL}/api/SSP/edit/completeQualificationDetails`,
                payload
            );

            const isSuccess = response?.data === true || String(response?.data).toLowerCase() === "true";
            if (!isSuccess) {
                return rejectWithValue({
                    engagementId_GUID: payload?.engagementId_GUID,
                    message: "Edit complete qualification details failed",
                });
            }

            return payload;
        } catch (error) {
            return rejectWithValue({
                engagementId_GUID: payload?.engagementId_GUID,
                message: error?.response?.data || error?.message || "Edit complete qualification details failed",
            });
        }
    }
);

export const fetchSSPQuestions = createAsyncThunk(
    "partnerFileUpload/fetchSSPQuestions",
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiQueryWithToken.get("/api/SSP/get/ssp/questions");
            const rawPayload =
                response?.data?.data ||
                response?.data?.result ||
                response?.data;

            const rawQuestions = Array.isArray(rawPayload)
                ? rawPayload
                : (Array.isArray(rawPayload?.questions) ? rawPayload.questions : []);

            const questions = rawQuestions
                .map((item, index) => normalizeQuestion(item, index))
                .filter(Boolean);

            return questions;
        } catch (error) {
            return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch SSP questions");
        }
    }
);

const engagementQualificationSlice = createSlice({
    name: "engagementQualification",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchEngagementQualification.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchEngagementQualification.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.data = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchEngagementQualification.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error?.message || "Failed to load engagement qualification";
            })
            .addCase(editEngagementQualification.fulfilled, (state, action) => {
                const updated = action.payload || {};
                const updatedQuestionAnswers = normalizeQuestionAnswers(updated?.questionAnswers);
                const fallbackNonGPSClient = getQuestionAnswerById(updatedQuestionAnswers, 1);
                const fallbackIsEYPrimeFirm = getQuestionAnswerById(updatedQuestionAnswers, 2);
                const fallbackIsEngagementServicesEnded = getQuestionAnswerById(updatedQuestionAnswers, 3);
                const fallbackIsEPApproved = getQuestionAnswerById(updatedQuestionAnswers, 4);

                state.data = state.data.map((item) => {
                    if (item.engagementId_GUID !== updated.engagementId_GUID) return item;
                    return {
                        ...item,
                        nonGPSClient: updated?.nonGPSClient ?? fallbackNonGPSClient ?? item.nonGPSClient,
                        isEYPrimeFirm: updated?.isEYPrimeFirm ?? fallbackIsEYPrimeFirm ?? item.isEYPrimeFirm,
                        isEngagementServicesEnded: updated?.isEngagementServicesEnded ?? fallbackIsEngagementServicesEnded ?? item.isEngagementServicesEnded,
                        isEPApproved: updated?.isEPApproved ?? fallbackIsEPApproved ?? item.isEPApproved,
                        questionAnswers: updatedQuestionAnswers.length > 0 ? updatedQuestionAnswers : item.questionAnswers,
                        finalDecision: updated?.finalDecision ?? item.finalDecision,
                    };
                });
            })
            .addCase(fetchCompleteQualificationDetails.pending, (state, action) => {
                const guid = action.meta.arg?.engagementId_GUID;
                if (!guid) return;
                state.completeQualificationStatusByGuid[guid] = "loading";
                state.completeQualificationErrorByGuid[guid] = null;
            })
            .addCase(fetchCompleteQualificationDetails.fulfilled, (state, action) => {
                const guid = action.payload?.engagementId_GUID;
                if (!guid) return;
                state.completeQualificationStatusByGuid[guid] = "succeeded";
                state.completeQualificationDetailsByGuid[guid] = action.payload?.details || {};
            })
            .addCase(fetchCompleteQualificationDetails.rejected, (state, action) => {
                const guid = action.payload?.engagementId_GUID || action.meta.arg?.engagementId_GUID;
                if (!guid) return;
                state.completeQualificationStatusByGuid[guid] = "failed";
                state.completeQualificationErrorByGuid[guid] = action.payload?.message || action.error?.message || "Failed to fetch complete qualification details";
            })
            .addCase(editCompleteQualificationDetails.pending, (state, action) => {
                const guid = action.meta.arg?.payload?.engagementId_GUID;
                if (!guid) return;
                state.completeQualificationSaveStatusByGuid[guid] = "loading";
                state.completeQualificationErrorByGuid[guid] = null;
            })
            .addCase(editCompleteQualificationDetails.fulfilled, (state, action) => {
                const guid = action.payload?.engagementId_GUID;
                if (!guid) return;
                state.completeQualificationSaveStatusByGuid[guid] = "succeeded";
                state.completeQualificationDetailsByGuid[guid] = {
                    ...(state.completeQualificationDetailsByGuid[guid] || {}),
                    ...action.payload,
                };
            })
            .addCase(editCompleteQualificationDetails.rejected, (state, action) => {
                const guid = action.payload?.engagementId_GUID || action.meta.arg?.payload?.engagementId_GUID;
                if (!guid) return;
                state.completeQualificationSaveStatusByGuid[guid] = "failed";
                state.completeQualificationErrorByGuid[guid] = action.payload?.message || action.error?.message || "Failed to save complete qualification details";
            })
            .addCase(fetchSSPQuestions.pending, (state) => {
                state.sspQuestionsStatus = "loading";
                state.sspQuestionsError = null;
            })
            .addCase(fetchSSPQuestions.fulfilled, (state, action) => {
                state.sspQuestionsStatus = "succeeded";
                state.sspQuestions = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchSSPQuestions.rejected, (state, action) => {
                state.sspQuestionsStatus = "failed";
                state.sspQuestionsError = action.payload || action.error?.message || "Failed to fetch SSP questions";
            });
    },
});

export default engagementQualificationSlice.reducer;
